import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, api } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { 
  User, Users, MapPin, Briefcase, ArrowRight, ArrowLeft, 
  Camera, Loader2, Check
} from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_social-circle-37/artifacts/vzzvahrb_IMG-20251227-WA0000.jpg";

const STEPS = [
  { id: 1, title: "Personal Details", icon: User, description: "Tell us about yourself" },
  { id: 2, title: "Family Details", icon: Users, description: "Your family information" },
  { id: 3, title: "Address", icon: MapPin, description: "Where do you live?" },
  { id: 4, title: "Business Details", icon: Briefcase, description: "Your work information" },
];

const INDIAN_STATES = [
  "Andhra Pradesh", "Arunachal Pradesh", "Assam", "Bihar", "Chhattisgarh",
  "Goa", "Gujarat", "Haryana", "Himachal Pradesh", "Jharkhand", "Karnataka",
  "Kerala", "Madhya Pradesh", "Maharashtra", "Manipur", "Meghalaya", "Mizoram",
  "Nagaland", "Odisha", "Punjab", "Rajasthan", "Sikkim", "Tamil Nadu",
  "Telangana", "Tripura", "Uttar Pradesh", "Uttarakhand", "West Bengal",
  "Delhi", "Jammu and Kashmir", "Ladakh", "Puducherry", "Chandigarh"
];

const FIXED_LAST_NAME = "LUNAWAT";

// List of countries for overseas users
const COUNTRIES = [
  "Afghanistan", "Albania", "Algeria", "Andorra", "Angola", "Argentina", "Armenia", "Australia",
  "Austria", "Azerbaijan", "Bahamas", "Bahrain", "Bangladesh", "Barbados", "Belarus", "Belgium",
  "Belize", "Benin", "Bhutan", "Bolivia", "Bosnia and Herzegovina", "Botswana", "Brazil", "Brunei",
  "Bulgaria", "Burkina Faso", "Burundi", "Cambodia", "Cameroon", "Canada", "Chad", "Chile", "China",
  "Colombia", "Comoros", "Congo", "Costa Rica", "Croatia", "Cuba", "Cyprus", "Czech Republic",
  "Denmark", "Djibouti", "Dominican Republic", "Ecuador", "Egypt", "El Salvador", "Estonia",
  "Ethiopia", "Fiji", "Finland", "France", "Gabon", "Gambia", "Georgia", "Germany", "Ghana", "Greece",
  "Guatemala", "Guinea", "Guyana", "Haiti", "Honduras", "Hungary", "Iceland", "Indonesia", "Iran",
  "Iraq", "Ireland", "Israel", "Italy", "Jamaica", "Japan", "Jordan", "Kazakhstan", "Kenya", "Kuwait",
  "Kyrgyzstan", "Laos", "Latvia", "Lebanon", "Libya", "Lithuania", "Luxembourg", "Madagascar",
  "Malawi", "Malaysia", "Maldives", "Mali", "Malta", "Mauritania", "Mauritius", "Mexico", "Moldova",
  "Monaco", "Mongolia", "Montenegro", "Morocco", "Mozambique", "Myanmar", "Namibia", "Nepal",
  "Netherlands", "New Zealand", "Nicaragua", "Niger", "Nigeria", "North Korea", "North Macedonia",
  "Norway", "Oman", "Pakistan", "Panama", "Papua New Guinea", "Paraguay", "Peru", "Philippines",
  "Poland", "Portugal", "Qatar", "Romania", "Russia", "Rwanda", "Saudi Arabia", "Senegal", "Serbia",
  "Singapore", "Slovakia", "Slovenia", "Somalia", "South Africa", "South Korea", "Spain", "Sri Lanka",
  "Sudan", "Suriname", "Sweden", "Switzerland", "Syria", "Taiwan", "Tajikistan", "Tanzania", "Thailand",
  "Togo", "Trinidad and Tobago", "Tunisia", "Turkey", "Turkmenistan", "Uganda", "Ukraine",
  "United Arab Emirates", "United Kingdom", "United States", "Uruguay", "Uzbekistan", "Venezuela",
  "Vietnam", "Yemen", "Zambia", "Zimbabwe"
];

// Blood group options
const BLOOD_GROUPS = ["A+", "A-", "B+", "B-", "AB+", "AB-", "O+", "O-"];

// Native places from Rajasthan
const RAJASTHAN_PLACES = [
  "Ajmer", "Alwar", "Banswara", "Baran", "Barmer", "Bharatpur", "Bhilwara", "Bikaner",
  "Bundi", "Chittorgarh", "Churu", "Dausa", "Dholpur", "Dungarpur", "Ganganagar",
  "Hanumangarh", "Jaipur", "Jaisalmer", "Jalore", "Jhalawar", "Jhunjhunu", "Jodhpur",
  "Karauli", "Kota", "Nagaur", "Pali", "Pratapgarh", "Rajsamand", "Sawai Madhopur",
  "Sikar", "Sirohi", "Tonk", "Udaipur", "Beawar", "Bhiwadi", "Didwana", "Fatehpur",
  "Gangapur City", "Hindaun", "Kishangarh", "Lachhmangarh", "Makrana", "Nathdwara",
  "Nawalgarh", "Nimbahera", "Nokha", "Phalodi", "Pilani", "Rajgarh", "Ratangarh",
  "Sadulpur", "Sanganer", "Sardarshahr", "Shahpura", "Sujangarh", "Suratgarh", "Other"
];

const OnboardingPage = () => {
  const { user, updateUser } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [prefixes, setPrefixes] = useState([]);
  
  // Initialize children array helper
  const initializeChildren = (count, existingChildren = []) => {
    const children = [];
    for (let i = 0; i < count; i++) {
      children.push(existingChildren[i] || {
        name: "",
        gender: "",
        age: "",
        education: "",
        marital_status: "",
        photo: ""
      });
    }
    return children;
  };
  
  const [formData, setFormData] = useState({
    // Personal
    avatar: user?.avatar || "",
    prefix: user?.prefix || "",
    first_name: user?.first_name || "",
    middle_name: user?.middle_name || "",
    username: user?.username || "",
    date_of_birth: user?.date_of_birth || "",
    gender: user?.gender || "",
    blood_group: user?.blood_group || "",
    native_place: user?.native_place || "",
    bio: user?.bio || "",
    // Family
    marital_status: user?.marital_status || "",
    spouse_name: user?.spouse_name || "",
    spouse_photo: user?.spouse_photo || "",
    date_of_marriage: user?.date_of_marriage || "",
    number_of_children: user?.number_of_children || 0,
    children: user?.children || [],
    father_name: user?.father_name || "",
    mother_name: user?.mother_name || "",
    // Address
    address_line1: user?.address_line1 || "",
    address_line2: user?.address_line2 || "",
    city: user?.city || "",
    state: user?.state || "",
    pincode: user?.pincode || "",
    country: user?.country || "India",
    is_overseas: user?.is_overseas || false,
    // Business
    occupation: user?.occupation || "",
    company_name: user?.company_name || "",
    designation: user?.designation || "",
    work_address: user?.work_address || "",
  });

  // Fetch prefixes on mount
  useEffect(() => {
    const fetchPrefixes = async () => {
      try {
        const response = await api.get("/prefixes");
        setPrefixes(response.data);
      } catch (error) {
        console.error("Failed to fetch prefixes", error);
      }
    };
    fetchPrefixes();
  }, []);

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleAvatarUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error("Please select an image file");
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image must be less than 5MB");
      return;
    }

    setUploadingAvatar(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      const response = await api.post('/upload', formDataUpload, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      updateField('avatar', response.data.url);
      toast.success("Photo uploaded!");
    } catch (_error) {
      toast.error("Failed to upload photo");
    } finally {
      setUploadingAvatar(false);
    }
  };

  // Handle photo upload for spouse or children
  const handleFamilyPhotoUpload = async (e, type, childIndex = null) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error("Please select an image file");
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image must be less than 5MB");
      return;
    }

    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      const response = await api.post('/upload', formDataUpload, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      if (type === 'spouse') {
        updateField('spouse_photo', response.data.url);
      } else if (type === 'child' && childIndex !== null) {
        updateChildField(childIndex, 'photo', response.data.url);
      }
      toast.success("Photo uploaded!");
    } catch (_error) {
      toast.error("Failed to upload photo");
    }
  };

  // Update a specific child's field
  const updateChildField = (index, field, value) => {
    setFormData(prev => {
      const newChildren = [...prev.children];
      newChildren[index] = { ...newChildren[index], [field]: value };
      return { ...prev, children: newChildren };
    });
  };

  // Handle number of children change
  const handleChildrenCountChange = (count) => {
    const numCount = parseInt(count);
    setFormData(prev => ({
      ...prev,
      number_of_children: numCount,
      children: initializeChildren(numCount, prev.children)
    }));
  };

  const validateStep = () => {
    switch (currentStep) {
      case 1:
        if (!formData.prefix) {
          toast.error("Please select a prefix");
          return false;
        }
        if (!formData.first_name.trim()) {
          toast.error("First name is required");
          return false;
        }
        if (!formData.date_of_birth) {
          toast.error("Date of birth is required");
          return false;
        }
        if (!formData.gender) {
          toast.error("Please select your gender");
          return false;
        }
        break;
      case 2:
        if (!formData.marital_status) {
          toast.error("Please select marital status");
          return false;
        }
        // Spouse name only required if married
        if (formData.marital_status === 'married' && !formData.spouse_name.trim()) {
          const spouseLabel = formData.gender === 'female' ? "Husband's name" : "Spouse name";
          toast.error(`${spouseLabel} is required for married status`);
          return false;
        }
        break;
      case 3:
        if (!formData.address_line1.trim()) {
          toast.error("Address is required");
          return false;
        }
        if (!formData.city.trim()) {
          toast.error("City is required");
          return false;
        }
        // Country required for overseas users
        if (formData.is_overseas && !formData.country) {
          toast.error("Please select your country");
          return false;
        }
        // State required only for non-overseas users
        if (!formData.is_overseas && !formData.state) {
          toast.error("Please select your state");
          return false;
        }
        // Pincode validation - required and 6 digits only for non-overseas
        if (!formData.is_overseas) {
          if (!formData.pincode.trim()) {
            toast.error("Pincode is required");
            return false;
          }
          if (formData.pincode.length !== 6) {
            toast.error("Pincode must be 6 digits");
            return false;
          }
        }
        break;
      case 4:
        if (!formData.occupation) {
          toast.error("Occupation is required");
          return false;
        }
        break;
    }
    return true;
  };

  // Final validation before submit - check ALL required fields
  const validateAllFields = () => {
    const errors = [];
    
    // Step 1 - Personal Details
    if (!formData.prefix) errors.push("Prefix");
    if (!formData.first_name.trim()) errors.push("First Name");
    if (!formData.date_of_birth) errors.push("Date of Birth");
    if (!formData.gender) errors.push("Gender");
    
    // Step 2 - Family Details
    if (!formData.marital_status) errors.push("Marital Status");
    // Spouse name only required if married
    if (formData.marital_status === 'married' && !formData.spouse_name.trim()) {
      errors.push(formData.gender === 'female' ? "Husband's Name" : "Spouse Name");
    }
    
    // Step 3 - Address
    if (!formData.address_line1.trim()) errors.push("Address Line 1");
    if (!formData.city.trim()) errors.push("City");
    if (formData.is_overseas) {
      if (!formData.country) errors.push("Country");
    } else {
      if (!formData.state) errors.push("State");
      if (!formData.pincode.trim() || formData.pincode.length !== 6) errors.push("Pincode (6 digits)");
    }
    
    // Step 4 - Business
    if (!formData.occupation) errors.push("Occupation");
    
    if (errors.length > 0) {
      toast.error(`Please fill all required fields: ${errors.join(", ")}`);
      return false;
    }
    return true;
  };

  const handleNext = () => {
    if (validateStep()) {
      setCurrentStep(prev => Math.min(prev + 1, 4));
    }
  };

  const handlePrevious = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const handleSubmit = async () => {
    // Validate current step first
    if (!validateStep()) return;
    
    // Validate ALL required fields before submission
    if (!validateAllFields()) return;

    setLoading(true);
    try {
      // Generate username from first name + last name if not set
      const fullName = `${formData.first_name} ${FIXED_LAST_NAME}`;
      const username = formData.username || fullName.toLowerCase().replace(/\s+/g, '_');
      
      const response = await api.put('/users/profile', {
        ...formData,
        username,
        last_name: FIXED_LAST_NAME,
        is_profile_complete: true,
        verification_status: "pending"
      });
      
      updateUser(response.data);
      toast.success("Profile submitted for verification!");
      navigate("/pending-verification");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save profile");
    } finally {
      setLoading(false);
    }
  };

  const getInitials = () => {
    if (formData.first_name) {
      return `${formData.first_name[0]}${FIXED_LAST_NAME[0]}`.toUpperCase();
    }
    return user?.phone?.slice(-2) || "??";
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-6">
            {/* Avatar Upload */}
            <div className="flex flex-col items-center gap-4">
              <div className="relative">
                <Avatar className="w-28 h-28 text-3xl">
                  <AvatarImage src={formData.avatar} alt="Avatar" />
                  <AvatarFallback className="bg-primary/10 text-primary text-2xl font-bold">
                    {getInitials()}
                  </AvatarFallback>
                </Avatar>
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploadingAvatar}
                  className="absolute bottom-0 right-0 w-10 h-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center shadow-lg hover:bg-primary/90 transition-colors disabled:opacity-50"
                  data-testid="upload-avatar-btn"
                >
                  {uploadingAvatar ? <Loader2 className="w-5 h-5 animate-spin" /> : <Camera className="w-5 h-5" />}
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleAvatarUpload}
                  className="hidden"
                />
              </div>
              <p className="text-sm text-muted-foreground">Upload your profile photo</p>
            </div>

            {/* Prefix + First Name Row */}
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-2">
                <label className="text-sm font-medium">Prefix *</label>
                <Select value={formData.prefix} onValueChange={(v) => updateField('prefix', v)}>
                  <SelectTrigger data-testid="prefix-select">
                    <SelectValue placeholder="Select" />
                  </SelectTrigger>
                  <SelectContent>
                    {prefixes.map(p => (
                      <SelectItem key={p.id} value={p.label}>{p.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="col-span-2 space-y-2">
                <label className="text-sm font-medium">First Name *</label>
                <Input
                  placeholder="Enter your first name"
                  value={formData.first_name}
                  onChange={(e) => updateField('first_name', e.target.value)}
                  data-testid="first-name-input"
                />
              </div>
            </div>

            {/* Middle Name */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Middle Name (optional)</label>
              <Input
                placeholder="Enter your middle name"
                value={formData.middle_name}
                onChange={(e) => updateField('middle_name', e.target.value)}
                data-testid="middle-name-input"
              />
            </div>

            {/* Last Name - Fixed */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Last Name</label>
              <Input
                value={FIXED_LAST_NAME}
                disabled
                className="bg-muted font-semibold"
                data-testid="last-name-input"
              />
              <p className="text-xs text-muted-foreground">Last name is fixed and cannot be changed</p>
            </div>

            {/* Username */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Username (optional)</label>
              <Input
                placeholder="Choose a username"
                value={formData.username}
                onChange={(e) => updateField('username', e.target.value)}
                data-testid="username-input"
              />
              <p className="text-xs text-muted-foreground">If left empty, we&apos;ll create one from your name</p>
            </div>

            {/* Date of Birth */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Date of Birth *</label>
              <Input
                type="date"
                value={formData.date_of_birth}
                onChange={(e) => updateField('date_of_birth', e.target.value)}
                max={new Date().toISOString().split('T')[0]}
                min="1940-01-01"
                className="w-full"
                data-testid="dob-input"
              />
              {formData.date_of_birth && (
                <p className="text-xs text-muted-foreground">
                  Selected: {new Date(formData.date_of_birth).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}
                </p>
              )}
            </div>

            {/* Gender */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Gender *</label>
              <Select value={formData.gender} onValueChange={(v) => updateField('gender', v)}>
                <SelectTrigger data-testid="gender-select">
                  <SelectValue placeholder="Select gender" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="male">Male</SelectItem>
                  <SelectItem value="female">Female</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Blood Group */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Blood Group (optional)</label>
              <Select value={formData.blood_group} onValueChange={(v) => updateField('blood_group', v)}>
                <SelectTrigger data-testid="blood-group-select">
                  <SelectValue placeholder="Select blood group" />
                </SelectTrigger>
                <SelectContent>
                  {BLOOD_GROUPS.map(bg => (
                    <SelectItem key={bg} value={bg}>{bg}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Native Place from Rajasthan */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Native Place (Rajasthan) (optional)</label>
              <Select value={formData.native_place} onValueChange={(v) => updateField('native_place', v)}>
                <SelectTrigger data-testid="native-place-select">
                  <SelectValue placeholder="Select native place" />
                </SelectTrigger>
                <SelectContent>
                  {RAJASTHAN_PLACES.map(place => (
                    <SelectItem key={place} value={place}>{place}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Bio */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Bio (optional)</label>
              <Textarea
                placeholder="Tell us a bit about yourself..."
                value={formData.bio}
                onChange={(e) => updateField('bio', e.target.value)}
                rows={3}
                data-testid="bio-input"
              />
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-6">
            {/* Marital Status */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Marital Status *</label>
              <Select value={formData.marital_status} onValueChange={(v) => updateField('marital_status', v)}>
                <SelectTrigger data-testid="marital-status-select">
                  <SelectValue placeholder="Select marital status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="single">Single</SelectItem>
                  <SelectItem value="married">Married</SelectItem>
                  <SelectItem value="divorced">Divorced</SelectItem>
                  <SelectItem value="widowed">Widowed</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Spouse Section - Only show if married */}
            {formData.marital_status === 'married' && (
              <div className="space-y-4 animate-fade-in p-4 border rounded-lg bg-muted/30">
                <h4 className="font-semibold text-sm">
                  {formData.gender === 'female' ? "Husband's Details" : "Spouse Details"}
                </h4>
                
                {/* Spouse Photo */}
                <div className="flex items-center gap-4">
                  <div className="relative">
                    <Avatar className="w-16 h-16">
                      <AvatarImage src={formData.spouse_photo} alt="Spouse" />
                      <AvatarFallback className="bg-primary/10 text-primary">
                        {formData.spouse_name ? formData.spouse_name[0]?.toUpperCase() : "S"}
                      </AvatarFallback>
                    </Avatar>
                    <label className="absolute bottom-0 right-0 w-6 h-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center cursor-pointer shadow-lg hover:bg-primary/90">
                      <Camera className="w-3 h-3" />
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleFamilyPhotoUpload(e, 'spouse')}
                        className="hidden"
                      />
                    </label>
                  </div>
                  <div className="flex-1 space-y-2">
                    <label className="text-sm font-medium">
                      {formData.gender === 'female' ? "Husband's Name" : "Spouse Name"} *
                    </label>
                    <Input
                      placeholder={formData.gender === 'female' ? "Enter husband's name" : "Enter spouse's name"}
                      value={formData.spouse_name}
                      onChange={(e) => updateField('spouse_name', e.target.value)}
                      data-testid="spouse-name-input"
                    />
                  </div>
                </div>
                
                {/* Date of Marriage */}
                <div className="space-y-2">
                  <label className="text-sm font-medium">Date of Marriage (optional)</label>
                  <Input
                    type="date"
                    value={formData.date_of_marriage}
                    onChange={(e) => updateField('date_of_marriage', e.target.value)}
                    max={new Date().toISOString().split('T')[0]}
                    min="1950-01-01"
                    className="w-full"
                    data-testid="marriage-date-input"
                  />
                  {formData.date_of_marriage && (
                    <p className="text-xs text-muted-foreground">
                      Selected: {new Date(formData.date_of_marriage).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Number of Children - Only show if not single */}
            {formData.marital_status && formData.marital_status !== "single" && (
              <>
                <div className="space-y-2">
                  <label className="text-sm font-medium">Number of Children</label>
                  <Select 
                    value={formData.number_of_children.toString()} 
                    onValueChange={handleChildrenCountChange}
                  >
                    <SelectTrigger data-testid="children-select">
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent>
                      {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(n => (
                        <SelectItem key={n} value={n.toString()}>{n}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Children Details */}
                {formData.number_of_children > 0 && (
                  <div className="space-y-4 animate-fade-in">
                    <h4 className="font-semibold text-sm text-muted-foreground">Children Details</h4>
                    {formData.children.map((child, index) => (
                      <div key={index} className="p-4 border rounded-lg bg-muted/30 space-y-4">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="w-6 h-6 rounded-full bg-primary text-primary-foreground text-xs flex items-center justify-center font-bold">
                            {index + 1}
                          </span>
                          <span className="font-medium text-sm">Child {index + 1}</span>
                        </div>
                        
                        {/* Child Photo and Name Row */}
                        <div className="flex items-start gap-4">
                          <div className="relative">
                            <Avatar className="w-14 h-14">
                              <AvatarImage src={child.photo} alt={`Child ${index + 1}`} />
                              <AvatarFallback className="bg-primary/10 text-primary text-sm">
                                {child.name ? child.name[0]?.toUpperCase() : (index + 1)}
                              </AvatarFallback>
                            </Avatar>
                            <label className="absolute bottom-0 right-0 w-5 h-5 rounded-full bg-primary text-primary-foreground flex items-center justify-center cursor-pointer shadow-lg hover:bg-primary/90">
                              <Camera className="w-2.5 h-2.5" />
                              <input
                                type="file"
                                accept="image/*"
                                onChange={(e) => handleFamilyPhotoUpload(e, 'child', index)}
                                className="hidden"
                              />
                            </label>
                          </div>
                          <div className="flex-1 space-y-2">
                            <label className="text-sm font-medium">Name</label>
                            <Input
                              placeholder={`Enter child ${index + 1}'s name`}
                              value={child.name || ''}
                              onChange={(e) => updateChildField(index, 'name', e.target.value)}
                            />
                          </div>
                        </div>
                        
                        {/* Gender and Age Row */}
                        <div className="grid grid-cols-2 gap-3">
                          <div className="space-y-2">
                            <label className="text-sm font-medium">Gender</label>
                            <Select 
                              value={child.gender || ''} 
                              onValueChange={(v) => updateChildField(index, 'gender', v)}
                            >
                              <SelectTrigger>
                                <SelectValue placeholder="Select gender" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="male">Male</SelectItem>
                                <SelectItem value="female">Female</SelectItem>
                                <SelectItem value="other">Other</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="space-y-2">
                            <label className="text-sm font-medium">Age</label>
                            <Input
                              type="number"
                              min="0"
                              max="100"
                              placeholder="Age"
                              value={child.age || ''}
                              onChange={(e) => updateChildField(index, 'age', e.target.value)}
                            />
                          </div>
                        </div>

                        {/* Marital Status Row */}
                        <div className="space-y-2">
                          <label className="text-sm font-medium">Marital Status</label>
                          <Select 
                            value={child.marital_status || ''} 
                            onValueChange={(v) => updateChildField(index, 'marital_status', v)}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Select" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="unmarried">Unmarried</SelectItem>
                              <SelectItem value="married">Married</SelectItem>
                              <SelectItem value="divorced">Divorced</SelectItem>
                              <SelectItem value="widowed">Widowed</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        
                        {/* Education */}
                        <div className="space-y-2">
                          <label className="text-sm font-medium">Education</label>
                          <Select 
                            value={child.education || ''} 
                            onValueChange={(v) => updateChildField(index, 'education', v)}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Select education level" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="pre-school">Pre-School</SelectItem>
                              <SelectItem value="primary">Primary School (1-5)</SelectItem>
                              <SelectItem value="middle">Middle School (6-8)</SelectItem>
                              <SelectItem value="high-school">High School (9-10)</SelectItem>
                              <SelectItem value="senior-secondary">Senior Secondary (11-12)</SelectItem>
                              <SelectItem value="undergraduate">Undergraduate</SelectItem>
                              <SelectItem value="graduate">Graduate</SelectItem>
                              <SelectItem value="post-graduate">Post Graduate</SelectItem>
                              <SelectItem value="doctorate">Doctorate/PhD</SelectItem>
                              <SelectItem value="professional">Professional Degree</SelectItem>
                              <SelectItem value="other">Other</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        );

      case 3:
        return (
          <div className="space-y-6">
            {/* Overseas Checkbox */}
            <div className="flex items-center space-x-3 p-4 border rounded-lg bg-muted/30">
              <input
                type="checkbox"
                id="is_overseas"
                checked={formData.is_overseas}
                onChange={(e) => {
                  updateField('is_overseas', e.target.checked);
                  if (e.target.checked) {
                    updateField('country', '');
                    updateField('state', '');
                  } else {
                    updateField('country', 'India');
                  }
                }}
                className="w-5 h-5 rounded border-gray-300 text-primary focus:ring-primary"
                data-testid="overseas-checkbox"
              />
              <label htmlFor="is_overseas" className="text-sm font-medium cursor-pointer">
                I am residing overseas (outside India)
              </label>
            </div>

            {/* Country Selection - Only shown for overseas users */}
            {formData.is_overseas && (
              <div className="space-y-2 animate-fade-in">
                <label className="text-sm font-medium">Country *</label>
                <Select value={formData.country} onValueChange={(v) => updateField('country', v)}>
                  <SelectTrigger data-testid="country-select">
                    <SelectValue placeholder="Select country" />
                  </SelectTrigger>
                  <SelectContent>
                    {COUNTRIES.map(country => (
                      <SelectItem key={country} value={country}>{country}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Address Line 1 */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Address Line 1 *</label>
              <Input
                placeholder="House/Flat No., Building Name"
                value={formData.address_line1}
                onChange={(e) => updateField('address_line1', e.target.value)}
                data-testid="address1-input"
              />
            </div>

            {/* Address Line 2 */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Address Line 2</label>
              <Input
                placeholder="Street, Landmark"
                value={formData.address_line2}
                onChange={(e) => updateField('address_line2', e.target.value)}
                data-testid="address2-input"
              />
            </div>

            {/* City */}
            <div className="space-y-2">
              <label className="text-sm font-medium">City *</label>
              <Input
                placeholder="Enter city"
                value={formData.city}
                onChange={(e) => updateField('city', e.target.value)}
                data-testid="city-input"
              />
            </div>

            {/* State - Only show for non-overseas users */}
            {!formData.is_overseas && (
              <div className="space-y-2">
                <label className="text-sm font-medium">State *</label>
                <Select value={formData.state} onValueChange={(v) => updateField('state', v)}>
                  <SelectTrigger data-testid="state-select">
                    <SelectValue placeholder="Select state" />
                  </SelectTrigger>
                  <SelectContent>
                    {INDIAN_STATES.map(state => (
                      <SelectItem key={state} value={state}>{state}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* State/Province - Text field for overseas users */}
            {formData.is_overseas && (
              <div className="space-y-2 animate-fade-in">
                <label className="text-sm font-medium">State/Province</label>
                <Input
                  placeholder="Enter state or province"
                  value={formData.state}
                  onChange={(e) => updateField('state', e.target.value)}
                  data-testid="state-input-overseas"
                />
              </div>
            )}

            {/* Pincode/Postal Code */}
            <div className="space-y-2">
              <label className="text-sm font-medium">{formData.is_overseas ? "Postal Code" : "Pincode *"}</label>
              <Input
                placeholder={formData.is_overseas ? "Enter postal code" : "Enter 6-digit pincode"}
                value={formData.pincode}
                onChange={(e) => {
                  if (formData.is_overseas) {
                    updateField('pincode', e.target.value.slice(0, 10));
                  } else {
                    updateField('pincode', e.target.value.replace(/\D/g, '').slice(0, 6));
                  }
                }}
                maxLength={formData.is_overseas ? 10 : 6}
                data-testid="pincode-input"
              />
            </div>

            {/* Country display for non-overseas */}
            {!formData.is_overseas && (
              <div className="space-y-2">
                <label className="text-sm font-medium">Country</label>
                <Input
                  value="India"
                  disabled
                  className="bg-muted"
                  data-testid="country-input"
                />
              </div>
            )}
          </div>
        );

      case 4:
        return (
          <div className="space-y-6">
            {/* Occupation */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Occupation *</label>
              <Select value={formData.occupation} onValueChange={(v) => updateField('occupation', v)}>
                <SelectTrigger data-testid="occupation-select">
                  <SelectValue placeholder="Select occupation" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="employed">Employed</SelectItem>
                  <SelectItem value="self-employed">Self Employed</SelectItem>
                  <SelectItem value="business">Business Owner</SelectItem>
                  <SelectItem value="student">Student</SelectItem>
                  <SelectItem value="homemaker">Homemaker</SelectItem>
                  <SelectItem value="retired">Retired</SelectItem>
                  <SelectItem value="freelancer">Freelancer</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Company Name - Show if employed or self-employed */}
            {['employed', 'self-employed', 'business', 'freelancer'].includes(formData.occupation) && (
              <>
                <div className="space-y-2 animate-fade-in">
                  <label className="text-sm font-medium">Company/Business Name</label>
                  <Input
                    placeholder="Enter company or business name"
                    value={formData.company_name}
                    onChange={(e) => updateField('company_name', e.target.value)}
                    data-testid="company-input"
                  />
                </div>

                <div className="space-y-2 animate-fade-in">
                  <label className="text-sm font-medium">Designation/Role</label>
                  <Input
                    placeholder="Enter your designation"
                    value={formData.designation}
                    onChange={(e) => updateField('designation', e.target.value)}
                    data-testid="designation-input"
                  />
                </div>

                <div className="space-y-2 animate-fade-in">
                  <label className="text-sm font-medium">Work Address</label>
                  <Textarea
                    placeholder="Enter your work address"
                    value={formData.work_address}
                    onChange={(e) => updateField('work_address', e.target.value)}
                    rows={2}
                    data-testid="work-address-input"
                  />
                </div>
              </>
            )}

            {/* Info about verification */}
            <div className="p-4 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-lg">
              <p className="text-sm text-amber-800 dark:text-amber-200">
                <strong>Note:</strong> After submitting your details, your profile will be reviewed by an administrator. 
                You will be able to access the platform once your profile is verified.
              </p>
            </div>
          </div>
        );
    }
  };

  return (
    <div className="min-h-screen bg-background py-8 px-4 relative" data-testid="onboarding-page">
      {/* Watermark */}
      <div className="fixed inset-0 pointer-events-none z-0 flex items-center justify-center">
        <img 
          src={LOGO_URL} 
          alt="Lunawat Foundation" 
          className="w-64 h-64 md:w-96 md:h-96 object-contain opacity-5"
        />
      </div>
      
      <div className="max-w-2xl mx-auto relative z-10">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-2 mb-4">
            <img src={LOGO_URL} alt="Logo" className="w-12 h-12 rounded-full object-cover" />
            <span className="text-2xl font-bold">LUNAWAT FOUNDATION</span>
          </div>
          <h1 className="text-2xl font-bold mb-2">Complete Your Profile</h1>
          <p className="text-muted-foreground">Help us know you better to connect with the right people</p>
        </div>

        {/* Progress Steps */}
        <div className="flex justify-between mb-8">
          {STEPS.map((step, index) => (
            <div key={step.id} className="flex flex-col items-center flex-1">
              <div className="flex items-center w-full">
                <div 
                  className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 transition-all ${
                    currentStep > step.id 
                      ? 'bg-primary text-primary-foreground' 
                      : currentStep === step.id 
                        ? 'bg-primary text-primary-foreground ring-4 ring-primary/20' 
                        : 'bg-muted text-muted-foreground'
                  }`}
                >
                  {currentStep > step.id ? (
                    <Check className="w-5 h-5" />
                  ) : (
                    <step.icon className="w-5 h-5" />
                  )}
                </div>
                {index < STEPS.length - 1 && (
                  <div className={`flex-1 h-1 mx-2 rounded ${currentStep > step.id ? 'bg-primary' : 'bg-muted'}`} />
                )}
              </div>
              <span className={`text-xs mt-2 text-center hidden sm:block ${currentStep === step.id ? 'text-primary font-medium' : 'text-muted-foreground'}`}>
                {step.title}
              </span>
            </div>
          ))}
        </div>

        {/* Form Card */}
        <Card className="shadow-lg">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              {(() => {
                const StepIcon = STEPS[currentStep - 1].icon;
                return <StepIcon className="w-5 h-5 text-primary" />;
              })()}
              {STEPS[currentStep - 1].title}
            </CardTitle>
            <CardDescription>{STEPS[currentStep - 1].description}</CardDescription>
          </CardHeader>
          <CardContent>
            {renderStepContent()}

            {/* Navigation Buttons */}
            <div className="flex gap-4 mt-8">
              {currentStep > 1 && (
                <Button
                  variant="outline"
                  onClick={handlePrevious}
                  className="flex-1"
                  data-testid="prev-btn"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Previous
                </Button>
              )}
              
              {currentStep < 4 ? (
                <Button
                  onClick={handleNext}
                  className="flex-1 btn-hover"
                  data-testid="next-btn"
                >
                  Next
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              ) : (
                <Button
                  onClick={handleSubmit}
                  disabled={loading}
                  className="flex-1 btn-hover"
                  data-testid="complete-btn"
                >
                  {loading ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <>
                      Submit for Verification
                      <Check className="w-4 h-4 ml-2" />
                    </>
                  )}
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default OnboardingPage;
